/**
 * Pure helpers for the CCM Wave 3 cross-scope resource copy/move destination
 * picker (handoff `ccm-lexide-copy-move-ui-handoff-2026-06-30.md` §5).
 *
 * The destination is a SCOPE, never a path: a target is either the per-user
 * org ("Global") workspace or a specific case (addressed by the full
 * `{clientId, matterId, caseId}` triple the runtime requires). These helpers
 * derive the selectable target list from the lawyer's case list + the current
 * scope, encoding the two load-bearing rules so they can't drift and so
 * they're unit-testable in the project's node env without a React mount:
 *
 *   1. EXCLUDE THE CURRENT SCOPE. A `case→case` copy onto the same case
 *      (same workspace + same path) is rejected server-side `INVALID_PARAMS`;
 *      a `case→org`/`org→case` is fine. So when the source is a case, that
 *      case is dropped from the case rows; when the source is the org scope,
 *      the org ("Global") row is dropped (never offer `org→org`).
 *   2. ONLY ADDRESSABLE CASES. The runtime's case address requires the full
 *      `{clientId, matterId, caseId}` triple — a `CaseListItem` missing
 *      `clientId`/`matterId` (pre-broadening rows) can't be a destination, so
 *      it's filtered out (it can't build a valid `to`).
 *
 * The org row is offered as a target EXCEPT when the source is already the
 * org scope (rule 1). `case→org` is the "copy my case work into my own
 * scratch" path; `org→case` (a promotion) is offered for every addressable
 * case.
 */

import type { CaseListItem } from "@/lib/lexserver-client";

/** The scope the copy/move is sourced FROM — drives the exclude-current-scope
 *  rule. For a case source we carry the caseId to drop it from the target
 *  list; for an org source the org row is dropped. */
export type SourceScope =
  | { scope: "case"; caseId: string }
  | { scope: "org" };

/** A selectable destination row in the picker. */
export type CopyMoveTarget =
  | {
      kind: "case";
      /** Destination caseId — the React-Query invalidation key segment. */
      caseId: string;
      /** Human label (the case title) for the picker row + the success toast. */
      title: string;
      status?: string;
      /** The full scope address the RPC `to` field carries. */
      address: { scope: "case"; clientId: string; matterId: string; caseId: string };
    }
  | {
      kind: "org";
      /** The org dest's invalidation key segment is the literal "org". */
      caseId: "org";
      /** Pre-localized "Global / General" label (caller passes it in). */
      title: string;
      address: { scope: "org" };
    };

/**
 * Build the selectable destination targets for the picker.
 *
 *   - `cases`     the lawyer's loaded case list (`useCases`).
 *   - `source`    the scope the resource is being copied/moved FROM.
 *   - `orgLabel`  the pre-localized "Global / General" row label.
 *
 * Cases without the full `{clientId, matterId}` parent ids are dropped (they
 * can't build a valid runtime address). The current scope is excluded per the
 * server-side same-workspace + `org→org` guards.
 */
export function buildCopyMoveTargets(
  cases: readonly CaseListItem[],
  source: SourceScope,
  orgLabel: string,
): CopyMoveTarget[] {
  const targets: CopyMoveTarget[] = [];

  // The org ("Global") row — offered for every source EXCEPT an org source
  // (no `org→org`).
  if (source.scope !== "org") {
    targets.push({ kind: "org", caseId: "org", title: orgLabel, address: { scope: "org" } });
  }

  for (const c of cases) {
    // A case destination needs the full triple — skip rows missing parent ids.
    if (!c.clientId || !c.matterId) continue;
    // Exclude the source case itself (same-workspace is rejected server-side).
    if (source.scope === "case" && c.caseId === source.caseId) continue;
    targets.push({
      kind: "case",
      caseId: c.caseId,
      title: c.title,
      status: c.status,
      address: {
        scope: "case",
        clientId: c.clientId,
        matterId: c.matterId,
        caseId: c.caseId,
      },
    });
  }

  return targets;
}

/**
 * The React-Query cache-key SEGMENT for a target — the literal "org" for the
 * org workspace, otherwise the dest caseId. Mirrors `scopeKeySegment` so the
 * invalidation in `useResourceCopyMove` keys exactly like `useCaseWorkspaceTree`
 * (`["case-workspace-tree", <segment>]`), keeping case ↔ org cache-distinct.
 */
export function targetKeySegment(target: CopyMoveTarget): string {
  return target.kind === "org" ? "org" : target.caseId;
}

/** The source scope's cache-key segment — the literal "org" for an org
 *  source, otherwise the source caseId. Mirrors `targetKeySegment` for the
 *  source side: `useResourceCopyMove` invalidates the source tree on a `move`
 *  with `["case-workspace-tree", <this segment>]`. */
export function sourceKeySegment(source: SourceScope): string {
  return source.scope === "org" ? "org" : source.caseId;
}
